import { INpsSummary } from '../models/nps-summary.model';
import { ISeriesPoint } from '../models/series-point.model';
import { hashTexto, limitar, pseudoAleatorio, sinAcentos } from '../models/deterministic.model';
import { desgloseDe, npsDeSucursal } from './jerarquia.data';

/* -------------------------------------------------------------------------- */
/*                             ANCHOR interfaces                              */
/* -------------------------------------------------------------------------- */

/** Fila de "Detalle de encuestas": una variable de satisfaccion con su CSAT. */
export interface IVariableCsat {
  variable: string;
  detractores: number;
  pasivos: number;
  promotores: number;
  csat: number;
}

/** Fila de "NPS - Ranking por region, zona y sucursal". */
export interface IRankingRegion {
  variable: string;
  detractores: number;
  pasivos: number;
  promotores: number;
  nps: number;
}

/** Fila de "NPS - Ranking por sucursal". */
export interface IRankingSucursal {
  clave: string;
  sucursal: string;
  detractores: number;
  pasivos: number;
  promotores: number;
  neto: number;
  netoComerciante: number;
  netoConsumidor: number;
}

/** Fila de "NPS - Detalle por monto de compra". */
export interface IMontoCompra {
  rango: string;
  porcentaje: number;
  detractores: number;
  pasivos: number;
  promotores: number;
  neto: number;
}

/* -------------------------------------------------------------------------- */
/*                           ANCHOR NPS por cliente                           */
/* -------------------------------------------------------------------------- */

export const NPS_COMERCIANTE: INpsSummary = {
  nps: 69,
  detractor: 9,
  pasivo: 13,
  promotor: 78,
  period: 'Agosto/2026',
};

export const NPS_CONSUMIDOR: INpsSummary = {
  nps: 76,
  detractor: 5,
  pasivo: 14,
  promotor: 81,
  period: 'Agosto/2026',
};

/** Ago-25 → Ago-26 con el nombre completo del mes, igual que el eje del original. */
const MESES_HISTORICO: Array<string> = [
  'Agosto 2025', 'Septiembre 2025', 'Octubre 2025', 'Noviembre 2025',
  'Diciembre 2025', 'Enero 2026', 'Febrero 2026', 'Marzo 2026',
  'Abril 2026', 'Mayo 2026', 'Junio 2026', 'Julio 2026',
  'Agosto 2026',
];

const serie = (valores: Array<number>, conteos: Array<number>): Array<ISeriesPoint> =>
  MESES_HISTORICO.map((label, i) => ({ label, value: valores[i], count: conteos[i] }));

/** El NPS del mes, no la ventana movil: el ultimo punto es el KPI de arriba. */
export const HISTORICO_COMERCIANTE: Array<ISeriesPoint> = serie(
  [60, 62, 60, 62, 64, 62, 64, 66, 67, 64, 68, 69, 69],
  [2462, 2363, 2523, 2655, 2808, 2084, 2397, 2615, 2727, 2543, 2825, 2938, 1070],
);

export const HISTORICO_CONSUMIDOR: Array<ISeriesPoint> = serie(
  [67, 68, 67, 69, 70, 68, 72, 72, 73, 71, 75, 75, 76],
  [4778, 4587, 4897, 5155, 5452, 4046, 4653, 5075, 5293, 4937, 5485, 5702, 2078],
);

/* -------------------------------------------------------------------------- */
/*                        ANCHOR detalle de encuestas                         */
/* -------------------------------------------------------------------------- */

export const DETALLE_ENCUESTAS: Array<IVariableCsat> = [
  { variable: 'La atención del personal',            detractores:  6, pasivos: 12, promotores: 82, csat: 76 },
  { variable: 'Rapidez en el servicio',              detractores:  9, pasivos: 14, promotores: 77, csat: 68 },
  { variable: 'Disponibilidad de productos',         detractores:  5, pasivos: 26, promotores: 69, csat: 64 },
  { variable: 'Percepción de los precios',           detractores:  9, pasivos: 24, promotores: 67, csat: 58 },
  { variable: 'Orden y limpieza de la tienda',       detractores:  3, pasivos: 15, promotores: 82, csat: 79 },
  { variable: 'Calidad de frutas y verduras',        detractores: 12, pasivos: 15, promotores: 73, csat: 61 },
  { variable: 'Calidad de la cremería',              detractores:  6, pasivos: 17, promotores: 77, csat: 71 },
  { variable: 'Calidad del huevo',                   detractores:  6, pasivos: 13, promotores: 81, csat: 75 },
  { variable: 'Calidad de chiles y semillas',        detractores:  6, pasivos: 16, promotores: 78, csat: 72 },
  { variable: 'Promociones y ofertas',               detractores:  9, pasivos: 23, promotores: 68, csat: 59 },
  { variable: 'Tiempo de espera en caja',            detractores: 14, pasivos: 18, promotores: 68, csat: 54 },
  { variable: 'Facilidad para encontrar productos',  detractores:  8, pasivos: 14, promotores: 78, csat: 70 },
];

/* -------------------------------------------------------------------------- */
/*                            ANCHOR consideracion                            */
/* -------------------------------------------------------------------------- */

/** Motivos declarados para elegir la tienda; suman 100. */
export const CONSIDERACION: Array<{ name: string; value: number }> = [
  { name: 'Precios competitivos', value: 23.4 },
  { name: 'Por cercanía', value: 19.8 },
  { name: 'Acostumbro a comprar desde hace tiempo ahí', value: 16.1 },
  { name: 'Las promociones me parecen buenas', value: 14.7 },
  { name: 'Me beneficia adquirir en Zorro los productos para mi negocio', value: 13.2 },
  { name: 'Porque un amigo o familiar me recomendó a Zorro', value: 8.6 },
  { name: 'Porque he escuchado de Zorro en diversos medios (camiones, radio, Redes Sociales, etc)', value: 4.2 },
];

/* -------------------------------------------------------------------------- */
/*                        ANCHOR ranking region / zona                        */
/* -------------------------------------------------------------------------- */

/** Promedio de las sucursales de cada region, con el arbol de `dimensions`. */
export const RANKING_REGION: Array<IRankingRegion> = [
  { variable: 'LEONARDO MAXIMO',   detractores:  4, pasivos: 16, promotores: 80, nps: 76 },
  { variable: 'ALFONSO VEGA',      detractores:  8, pasivos: 10, promotores: 82, nps: 74 },
  { variable: 'OTILIO MACHUCA',    detractores:  6, pasivos: 15, promotores: 79, nps: 73 },
  { variable: 'ATANACIO MENDOZA',  detractores:  8, pasivos: 13, promotores: 79, nps: 71 },
  { variable: 'MARTIN MARIN',      detractores:  5, pasivos: 21, promotores: 74, nps: 69 },
];

/* -------------------------------------------------------------------------- */
/*                        ANCHOR ranking por sucursal                         */
/* -------------------------------------------------------------------------- */

/** Las 10 primeras claves son las que muestra el tablero original. */
const SUCURSALES_BASE: Array<IRankingSucursal> = [
  { clave: 'CCA', sucursal: 'SUCURSAL CAZADOR',            detractores:  6, pasivos: 13, promotores: 81, neto: 75, netoComerciante: 67, netoConsumidor: 81 },
  { clave: 'CNA', sucursal: 'SUCURSAL CAZADOR NAUCALPAN',  detractores:  8, pasivos: 15, promotores: 77, neto: 69, netoComerciante: 62, netoConsumidor: 74 },
  { clave: 'PSU', sucursal: 'SUCURSAL PROFETA',            detractores: 10, pasivos: 29, promotores: 61, neto: 51, netoComerciante: 49, netoConsumidor: 52 },
  { clave: 'Z11', sucursal: 'SUCURSAL AV. 11 SUR',         detractores:  5, pasivos: 24, promotores: 71, neto: 66, netoComerciante: 62, netoConsumidor: 71 },
  { clave: 'ZA2', sucursal: 'SUCURSAL ATIZAPÁN 2',         detractores:  4, pasivos: 13, promotores: 83, neto: 79, netoComerciante: 77, netoConsumidor: 83 },
  { clave: 'ZA3', sucursal: 'SUCURSAL ALTA TENSIÓN 3',     detractores:  2, pasivos:  9, promotores: 89, neto: 87, netoComerciante: 81, netoConsumidor: 89 },
  { clave: 'ZAC', sucursal: 'SUCURSAL AMECAMECA',          detractores:  2, pasivos: 12, promotores: 86, neto: 84, netoComerciante: 77, netoConsumidor: 85 },
  { clave: 'ZAG', sucursal: 'SUCURSAL ARAGÓN',             detractores:  8, pasivos: 16, promotores: 76, neto: 68, netoComerciante: 61, netoConsumidor: 70 },
  { clave: 'ZAH', sucursal: 'SUCURSAL AZTAHUACÁN',         detractores:  3, pasivos: 16, promotores: 81, neto: 78, netoComerciante: 73, netoConsumidor: 84 },
  { clave: 'ZAL', sucursal: 'SUCURSAL ALTA TENSIÓN',       detractores:  6, pasivos: 21, promotores: 73, neto: 67, netoComerciante: 60, netoConsumidor: 73 },
];

const LOCALIDADES: Array<string> = [
  'ACAMBAY', 'ACOLMAN', 'AHUEHUETES', 'AJUSCO', 'ALLENDE', 'ALMOLOYA', 'ALTAMIRA',
  'AMATEPEC', 'ANÁHUAC', 'APAN', 'APASEO', 'APIZACO', 'ARBOLEDAS', 'ATLACOMULCO',
  'ATLIXCO', 'ATOTONILCO', 'AXAPUSCO', 'AZCAPOTZALCO', 'BALBUENA', 'BOSQUES DE ARAGÓN',
  'BUENAVISTA', 'CALPULALPAN', 'CAMPESTRE', 'CANDELARIA', 'CARDONAL', 'CASA BLANCA',
  'CELAYA', 'CENTRO HISTÓRICO', 'CERRO GORDO', 'CHAPULTEPEC', 'CHICOLOAPAN',
  'CHIMALHUACÁN', 'CHOLULA', 'COACALCO', 'COAPA', 'COATEPEC', 'COLÓN', 'CONSTITUYENTES',
  'COPILCO', 'CORREGIDORA', 'COYOACÁN', 'CUAJIMALPA', 'CUAUTITLÁN', 'CUAUTLA',
  'CUERNAVACA', 'CULHUACÁN', 'DOCTORES', 'DOLORES HIDALGO', 'ECATZINGO', 'EL ORO',
  'EL SALTO', 'ERMITA', 'ESCANDÓN', 'FRESNILLO', 'GUADALUPE', 'GUSTAVO A. MADERO',
  'HUEHUETOCA', 'HUEJUTLA', 'HUIXQUILUCAN', 'IRAPUATO', 'IXTAPALUCA', 'IXTLAHUACA',
  'IZTACALCO', 'IZTAPALAPA', 'JALTENCO', 'JILOTEPEC', 'JIQUIPILCO', 'JIUTEPEC',
  'JOCOTITLÁN', 'JUÁREZ', 'LA PAZ', 'LA VILLA', 'LERMA', 'LINDAVISTA', 'LOMAS VERDES',
  'LOS REYES', 'MAGDALENA', 'MALINALCO', 'METEPEC', 'MEXICALTZINGO', 'MILPA ALTA',
  'MINERAL DE LA REFORMA', 'MIXCOAC', 'MIXQUIAHUALA', 'MOLINO DE ROSAS', 'MORELIA',
  'NAUCALPAN', 'NEZAHUALCÓYOTL', 'NICOLÁS ROMERO', 'NOPALTEPEC', 'OCOYOACAC',
  'OJO DE AGUA', 'OTUMBA', 'PACHUCA', 'PANTITLÁN', 'PARAÍSO', 'PASEOS DE CHURUBUSCO',
  'PATRIOTISMO', 'PEDREGAL', 'PEÑÓN', 'PERALVILLO', 'PERINORTE', 'PIEDAD', 'PORTALES',
  'PROGRESO', 'PUEBLA 2', 'QUERÉTARO 2', 'RÍO FRÍO', 'ROMA SUR', 'SALAMANCA',
  'SAN AGUSTÍN', 'SAN ANTONIO', 'SAN BUENAVENTURA', 'SAN CRISTÓBAL', 'SAN FELIPE',
  'SAN JUAN DEL RÍO', 'SAN MARTÍN', 'SAN MATEO', 'SAN MIGUEL', 'SANTA CLARA', 'SANTA FE',
  'SANTA MARTHA', 'SILAO', 'TACUBA', 'TALLERES', 'TECÁMAC', 'TECAMACHALCO', 'TEJUPILCO',
  'TENANGO', 'TEOLOYUCAN', 'TEOTIHUACÁN', 'TEPEJI', 'TEPOTZOTLÁN', 'TEQUISQUIAPAN',
  'TEXCOCO', 'TIZAYUCA', 'TLÁHUAC', 'TLALNEPANTLA', 'TLALPAN', 'TOLLOCAN', 'TULA',
  'TULANCINGO', 'VALLE DE BRAVO', 'VALLE DORADO', 'VILLA DEL CARBÓN', 'XALOSTOC',
  'XOCHIMILCO', 'ZACATEPEC', 'ZINACANTEPEC', 'ZUMPANGO',
];

const clavesUsadas = new Set<string>(SUCURSALES_BASE.map((s) => s.clave));

const claveDe = (nombre: string): string => {
  const base = sinAcentos(nombre).toUpperCase().replace(/[^A-Z0-9]/g, '');
  let clave = base.slice(0, 3).padEnd(3, 'X');
  const alfabeto = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let i = 0;

  while (clavesUsadas.has(clave)) {
    clave = `${base.slice(0, 2).padEnd(2, 'X')}${alfabeto[i % alfabeto.length]}`;
    i++;
  }

  clavesUsadas.add(clave);

  return clave;
};

/**
 * Las 135 sucursales que el ranking necesita y el resto de tablas no menciona.
 *
 * El neto NO se sortea aqui: sale de `npsDeSucursal()`, la misma funcion que
 * usa el NPS por sucursal del Home. Asi una tienda que aparece en las dos
 * pantallas trae la misma cifra en las dos, que es lo primero que revisa quien
 * conoce su sucursal.
 */
const generarSucursales = (cantidad: number): Array<IRankingSucursal> =>
  LOCALIDADES.slice(0, cantidad).map((localidad) => {
    const sucursal = `SUCURSAL ${localidad}`;
    const neto = npsDeSucursal(sucursal);
    const { detractores, pasivos, promotores } = desgloseDe(neto, `RK${sucursal}`);
    const tipo = pseudoAleatorio(hashTexto(`TC${sucursal}`));

    return {
      clave: claveDe(localidad),
      sucursal,
      detractores,
      pasivos,
      promotores,
      neto,
      netoComerciante: limitar(neto - 2 - Math.round(tipo() * 6), 20, 95),
      netoConsumidor: limitar(neto + 1 + Math.round(tipo() * 6), 20, 95),
    };
  });

export const RANKING_SUCURSAL: Array<IRankingSucursal> = [
  ...SUCURSALES_BASE,
  ...generarSucursales(135),
];

/* -------------------------------------------------------------------------- */
/*                           ANCHOR monto de compra                           */
/* -------------------------------------------------------------------------- */

export const MONTO_COMPRA: Array<IMontoCompra> = [
  { rango: '1 - $1.00 - $499.99',        porcentaje: 29.74, detractores:  4, pasivos: 16, promotores: 80, neto: 76 },
  { rango: '2 - $500.00 - $1,999.99',    porcentaje: 47.35, detractores:  5, pasivos: 15, promotores: 80, neto: 75 },
  { rango: '3 - $2,000.00 - $4,999.99',  porcentaje: 14.06, detractores:  9, pasivos: 11, promotores: 80, neto: 71 },
  { rango: '4 - $5,000.00 +',            porcentaje:  3.41, detractores: 10, pasivos: 16, promotores: 74, neto: 64 },
  { rango: '5 - OTRO',                   porcentaje:  5.44, detractores:  5, pasivos: 11, promotores: 84, neto: 79 },
];

/* -------------------------------------------------------------------------- */
/*                       ANCHOR historico de las tablas                       */
/* -------------------------------------------------------------------------- */

/** 19 etiquetas Feb-25 → Ago-26, el universo completo del tablero. */
export const ETIQUETAS_HISTORICO: Array<string> = [
  'Feb-25', 'Mar-25', 'Abr-25', 'May-25', 'Jun-25', 'Jul-25', 'Ago-25',
  'Sep-25', 'Oct-25', 'Nov-25', 'Dic-25', 'Ene-26', 'Feb-26', 'Mar-26',
  'Abr-26', 'May-26', 'Jun-26', 'Jul-26', 'Ago-26',
];

/** Serie de 19 meses que oscila alrededor del valor actual de la fila. */
export const historicoDe = (valor: number): Array<ISeriesPoint> => {
  const rnd = pseudoAleatorio(Math.round(valor * 7919) + 13);

  return ETIQUETAS_HISTORICO.map((label, i) => {
    const tendencia = (i - ETIQUETAS_HISTORICO.length + 1) * 0.4;
    const ruido = Math.round((rnd() - 0.5) * 9);

    return { label, value: limitar(Math.round(valor + tendencia + ruido), 25, 96) };
  });
};
